import type { Rule, RuleQueryMatch } from "@rebuild/shared";
import RestAPI from "../utils/restAPI";

const ROUTE = "/rest/tools/rules";

// The server generates the uuid on create and takes it from the path on update, so it is never sent in the body.
// `auditTrail` is server-owned (generated in the Rules service), so clients never send it. `note` is a required
// free-text explanation for the change; the server moves it into the generated audit-trail event.
export type SaveRuleBody = Omit<Rule, "uuid" | "auditTrail"> & { note: string };

function requireResult<T>(result: T | null | undefined, action: string): T {
  if (result == null) {
    throw new Error(`Failed to ${action} rule`);
  }
  return result;
}

export async function fetchRules(): Promise<Rule[]> {
  // With no query params the endpoint returns every rule as a match; unwrap each match down to its rule. Drafts are
  // opt-in because they're only relevant to this authoring UI.
  const matches: RuleQueryMatch[] = requireResult(
    await RestAPI.fetchRebuildAPI(ROUTE, "GET", { includeDrafts: true }, "data", true, false),
    "fetch",
  );
  return matches.map((match) => match.rule);
}

export async function createRule(body: SaveRuleBody): Promise<Rule> {
  return requireResult(await RestAPI.fetchRebuildAPI(ROUTE, "POST", body, "data", true, false), "create");
}

export async function updateRule(uuid: string, body: SaveRuleBody): Promise<Rule> {
  return requireResult(await RestAPI.fetchRebuildAPI(`${ROUTE}/${uuid}`, "PATCH", body, "data", true, false), "update");
}
